import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LeadDialog } from "@/components/LeadDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Minus, CircleNotch, CheckCircle, Star, Phone, Truck, Handshake, Package, ArrowsLeftRight } from "@phosphor-icons/react";
import { createLead } from "@/lib/api";

const PRICING = {
  carriers: {
    label: "Carriers",
    icon: Truck,
    blurb: "Find the best loads first — the fastest freight-finder with instant refresh and automated filters.",
    plans: [
      { id: "carrier-starter", name: "Starter", price: 49, rec: "Owner-operators getting started" },
      { id: "carrier-pro", name: "Pro", price: 149, popular: true, rec: "Owner-operators · 1–3 trucks" },
      { id: "carrier-fleet", name: "Fleet", price: 299, rec: "Small fleets · 3+ trucks" },
    ],
    features: [
      { key: "search", label: "Load search & truck posting" },
      { key: "app", label: "Mobile app access" },
      { key: "alarms", label: "Load match alarms" },
      { key: "credit", label: "Broker credit data" },
      { key: "counts", label: "Load counts by state" },
      { key: "exact", label: "Exact match alarms" },
      { key: "lanes", label: "Lane rate insights" },
      { key: "tabs", label: "Saved search tabs" },
      { key: "trihaul", label: "Triangular routing (TriHaul)" },
      { key: "preferred", label: "Preferred & blocked companies" },
      { key: "assurance", label: "Payment assurance support" },
      { key: "market", label: "Market conditions tool" },
    ],
    matrix: {
      "carrier-starter": { search: true, app: true, alarms: true, credit: true, counts: true, exact: true, lanes: "Basic", tabs: "1", trihaul: false, preferred: false, assurance: false, market: false },
      "carrier-pro": { search: true, app: true, alarms: true, credit: true, counts: true, exact: true, lanes: "7-day rates", tabs: "3", trihaul: true, preferred: true, assurance: true, market: false },
      "carrier-fleet": { search: true, app: true, alarms: true, credit: true, counts: true, exact: true, lanes: "Best available", tabs: "10", trihaul: true, preferred: true, assurance: true, market: true },
    },
  },
  brokers: {
    label: "Brokers",
    icon: Handshake,
    blurb: "Build your brokerage — expand your carrier network and cover any load with confidence.",
    plans: [
      { id: "broker-standard", name: "Broker", price: 159, rec: "New & growing brokerages" },
      { id: "broker-pro", name: "Broker Pro", price: 259, popular: true, rec: "Established brokerages" },
    ],
    features: [
      { key: "post", label: "Unlimited load posting" },
      { key: "network", label: "Carrier network access" },
      { key: "credit", label: "Carrier credit & authority data" },
      { key: "lanes", label: "LaneMakers rate tool" },
      { key: "rates", label: "Freight rate benchmarks" },
      { key: "priority", label: "Priority booking tools" },
      { key: "market", label: "Market conditions tool" },
      { key: "contract", label: "Contract lane rate data" },
    ],
    matrix: {
      "broker-standard": { post: true, network: true, credit: true, lanes: "Basic", rates: "15-day", priority: false, market: false, contract: false },
      "broker-pro": { post: true, network: true, credit: true, lanes: "Premium", rates: "Best available", priority: true, market: true, contract: true },
    },
  },
  combo: {
    label: "Combo",
    icon: ArrowsLeftRight,
    blurb: "The best of both worlds — full carrier + broker access in one subscription.",
    plans: [
      { id: "combo-all", name: "Carrier + Broker", price: 199, popular: true, rec: "Dispatchers & hybrid operations",
        bullets: ["Everything in Carrier Pro", "Everything in Broker Standard", "Unified load search & posting", "Combined credit & rate data", "Priority support"] },
    ],
  },
  shippers: {
    label: "Shippers",
    icon: Package,
    blurb: "Shipper central — reach the largest source of capacity and move freight faster.",
    plans: [
      { id: "shipper-custom", name: "Shipper", price: null, contact: true, rec: "Volume shippers & manufacturers",
        bullets: ["Access to vetted carrier capacity", "Post loads to the full board", "Dedicated account support", "Custom volume pricing", "Market & lane analytics"] },
    ],
  },
};

const TABS = ["carriers", "brokers", "combo", "shippers"];

export default function Pricing() {
  const location = useLocation();
  const [tab, setTab] = useState("carriers");
  const [subscribe, setSubscribe] = useState(null); // {plan, audience}
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(location.search).get("for");
    if (p && TABS.includes(p)) setTab(p);
  }, [location.search]);

  const data = PRICING[tab];

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      <Navbar onQuote={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section className="pt-32 pb-16 border-b border-white/10 bg-[#111A2E] relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#2563EB]/40 bg-[#2563EB]/10 text-[#60A5FA] text-xs uppercase tracking-[0.2em] mb-6">
            <Star size={14} weight="fill" className="text-[#2563EB]" /> Load Board Plans & Pricing
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-5">Grow your business for the long haul</h1>
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">Get the most relevant matches for your business — the right load for the right truck at the right price, no matter where you are.</p>
          <p className="text-sm text-[#64748b] mt-6 flex items-center justify-center gap-2"><Phone size={15} className="text-[#2563EB]" /> Need help deciding? Call (346) 820-6604</p>
        </div>
      </section>

      {/* TABS */}
      <div className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10">
        <div className="flex flex-wrap justify-center gap-2 bg-[#0B1220] border border-white/10 rounded-2xl p-2 shadow-xl w-fit mx-auto">
          {TABS.map((t) => {
            const Icon = PRICING[t].icon;
            const active = tab === t;
            return (
              <button key={t} data-testid={`pricing-tab-${t}`} onClick={() => setTab(t)}
                className={`px-5 sm:px-7 py-3 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${active ? "bg-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"}`}>
                <Icon size={18} weight="bold" /> {PRICING[t].label}
              </button>
            );
          })}
        </div>
      </div>

      {/* PLANS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <p className="text-center text-[#94A3B8] max-w-2xl mx-auto mb-12">{data.blurb}</p>

        <div className={`grid gap-6 ${data.plans.length === 1 ? "max-w-md mx-auto" : data.plans.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
          {data.plans.map((p) => (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative rounded-2xl p-8 border transition-transform duration-300 hover:-translate-y-1 ${p.popular ? "bg-[#111A2E] border-[#2563EB] shadow-[0_0_40px_rgba(37,99,235,0.25)]" : "bg-[#111A2E] border-white/10"}`}>
              {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#2563EB] text-white text-[11px] font-bold uppercase tracking-wider">Most Popular</div>}
              <h3 className="font-display text-2xl font-bold tracking-tight">{p.name}</h3>
              <p className="text-xs text-[#64748b] mt-1 mb-5 h-8">{p.rec}</p>
              <div className="mb-6">
                {p.contact ? (
                  <div className="font-display text-3xl font-extrabold text-[#60A5FA]">Contact for pricing</div>
                ) : (
                  <div className="flex items-end gap-1">
                    <span className="font-display text-5xl font-extrabold tracking-tighter">${p.price}</span>
                    <span className="text-[#94A3B8] mb-2">/mo</span>
                  </div>
                )}
              </div>
              <button data-testid={`subscribe-btn-${p.id}`} onClick={() => setSubscribe({ plan: p, audience: data.label })}
                className={`w-full py-3 rounded-full font-semibold transition-colors ${p.popular ? "bg-[#2563EB] text-white hover:bg-[#1D4ED8]" : "bg-white/5 border border-white/15 text-white hover:border-[#2563EB]"}`}>
                {p.contact ? "Contact Sales" : "Get Started"}
              </button>
              {p.bullets && (
                <ul className="mt-6 space-y-3">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#cbd5e1]"><Check size={16} weight="bold" className="text-[#10B981] mt-0.5 shrink-0" /> {b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* COMPARISON TABLE */}
        {data.features && (
          <div className="mt-16 overflow-x-auto rounded-2xl border border-white/10 bg-[#111A2E]">
            <table className="w-full text-sm min-w-[640px]" data-testid="pricing-comparison-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-6 py-4 text-[#94A3B8] font-medium">What's included</th>
                  {data.plans.map((p) => (
                    <th key={p.id} className="px-6 py-4 text-center">
                      <div className="font-display font-bold">{p.name}</div>
                      <div className="text-xs text-[#64748b] font-normal">{p.contact ? "Custom" : `$${p.price}/mo`}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.features.map((f, i) => (
                  <tr key={f.key} className={i % 2 ? "bg-white/[0.02]" : ""}>
                    <td className="px-6 py-3.5 text-[#cbd5e1]">{f.label}</td>
                    {data.plans.map((p) => {
                      const v = data.matrix[p.id][f.key];
                      return (
                        <td key={p.id} className="px-6 py-3.5 text-center">
                          {v === true ? <Check size={18} weight="bold" className="text-[#10B981] mx-auto" />
                            : v === false ? <Minus size={16} className="text-[#475569] mx-auto" />
                            : <span className="text-xs text-[#94A3B8]">{v}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* LEARN MORE CTA */}
      <section className="border-t border-white/10 bg-[#111A2E]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-4">Want to learn more?</h2>
          <p className="text-[#94A3B8] mb-8">Not sure which plan fits? Talk to a Houston dispatch specialist and we'll match you to the right package.</p>
          <button onClick={() => setQuoteOpen(true)} data-testid="pricing-contact-btn" className="px-8 py-3.5 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors">Talk to Sales</button>
        </div>
      </section>

      <Footer />

      <SubscribeDialog info={subscribe} onOpenChange={(v) => !v && setSubscribe(null)} />
      <LeadDialog open={quoteOpen} onOpenChange={setQuoteOpen} type="quote" />
    </div>
  );
}

function SubscribeDialog({ info, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setLoading(true);
    try {
      await createLead({
        type: "subscription", name: form.name, email: form.email, phone: form.phone || "",
        payload: { plan: info.plan.name, audience: info.audience, price: info.plan.contact ? "Custom" : `$${info.plan.price}/mo`, company: form.company || "" },
      });
      setDone(true);
      toast.success("Received! We'll send your onboarding & payment link.");
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setLoading(false); }
  };

  const close = (v) => { onOpenChange(v); if (!v) setTimeout(() => { setDone(false); setForm({}); }, 200); };

  if (!info) return null;
  const { plan, audience } = info;

  return (
    <Dialog open={!!info} onOpenChange={close}>
      <DialogContent className="bg-[#111A2E] border-white/10 text-white max-w-lg" data-testid="subscribe-dialog">
        {done ? (
          <div className="py-10 text-center flex flex-col items-center gap-4" data-testid="subscribe-success">
            <CheckCircle size={56} weight="fill" className="text-[#2563EB]" />
            <h3 className="font-display text-2xl font-bold">You're on board</h3>
            <p className="text-[#94A3B8] max-w-sm">We received your interest in the <b className="text-white">{audience} · {plan.name}</b> plan. Our team will email your onboarding steps and secure payment link shortly.</p>
            <button onClick={() => close(false)} className="mt-2 px-6 py-2.5 rounded-full bg-[#2563EB] text-white font-semibold" data-testid="subscribe-close-btn">Done</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">Get started — {plan.name}</DialogTitle>
              <DialogDescription className="text-[#94A3B8]">
                {audience} plan · <span className="text-[#60A5FA] font-semibold">{plan.contact ? "Custom pricing" : `$${plan.price}/mo`}</span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <Fld label="Full Name *"><Input data-testid="sub-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-[#0B1220] border-white/10" /></Fld>
                <Fld label="Phone"><Input data-testid="sub-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-[#0B1220] border-white/10" /></Fld>
              </div>
              <Fld label="Email *"><Input type="email" data-testid="sub-email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-[#0B1220] border-white/10" /></Fld>
              <Fld label="Company"><Input data-testid="sub-company" value={form.company || ""} onChange={(e) => set("company", e.target.value)} className="bg-[#0B1220] border-white/10" /></Fld>
              <button type="submit" disabled={loading} data-testid="sub-submit-btn" className="w-full py-3 rounded-full bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <CircleNotch size={18} className="animate-spin" />} {plan.contact ? "Request Pricing" : "Continue"}
              </button>
              <p className="text-xs text-[#64748b] text-center">Secure Stripe billing connects at checkout. No charge until you confirm.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-[#94A3B8]">{label}</Label>{children}</div>
);
